// src/utils/info-tooltip.js
// Comportamiento unificado para íconos de información ⓘ:
// hover, focus, click-toggle, Escape, click-outside.
// El tooltip se renderiza en document.body (portal) para garantizar z-index
// correcto incluso durante el onboarding (overlay en z-index 9000-9002).

const PORTAL_CLASS = 'info-tooltip-portal';
const Z_INDEX      = 9003;
const MAX_W        = 268;
const GAP          = 8;

let _portal    = null;
let _trigger   = null;
let _hideTimer = null;

function _clearTimer() {
    if (_hideTimer !== null) { clearTimeout(_hideTimer); _hideTimer = null; }
}

function _pos(trigger) {
    const r    = trigger.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.right - MAX_W, window.innerWidth - MAX_W - 8));
    const below = window.innerHeight - r.bottom - GAP;
    const above = r.top - GAP;
    if (below >= 56 || below >= above) {
        return { top: `${r.bottom + GAP}px`, left: `${left}px` };
    }
    return { bottom: `${window.innerHeight - r.top + GAP}px`, left: `${left}px` };
}

function _showPortal(trigger, contentEl) {
    _clearTimer();
    if (_portal) { _portal.remove(); _portal = null; }

    const portal = document.createElement('div');
    portal.className = PORTAL_CLASS;
    portal.setAttribute('role', 'tooltip');
    Array.from(contentEl.childNodes).forEach(n => portal.appendChild(n.cloneNode(true)));

    Object.assign(portal.style, { position: 'fixed', zIndex: String(Z_INDEX), maxWidth: `${MAX_W}px` });
    const pos = _pos(trigger);
    if (pos.top)    portal.style.top    = pos.top;
    if (pos.bottom) portal.style.bottom = pos.bottom;
    portal.style.left = pos.left;

    document.body.appendChild(portal);
    _portal  = portal;
    _trigger = trigger;
}

function _hidePortal() {
    _clearTimer();
    _portal?.remove();
    _portal  = null;
    _trigger = null;
}

function _scheduleHide(trigger) {
    _clearTimer();
    _hideTimer = setTimeout(() => { if (_trigger === trigger) _hidePortal(); }, 100);
}

function _isOpen(trigger) { return _trigger === trigger && !!_portal; }

// ── Handlers globales — se registran una sola vez ──────────────

let _globalInit = false;

function _ensureGlobal() {
    if (_globalInit) return;
    _globalInit = true;

    window.addEventListener('cm:close-info-tooltips', () => { if (_portal) _hidePortal(); });

    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && _portal) {
            const t = _trigger;
            _hidePortal();
            t?.focus();
        }
    });

    document.addEventListener('mousedown', e => {
        if (!_portal) return;
        if (_portal.contains(e.target)) return;
        if (_trigger && (_trigger === e.target || _trigger.contains(e.target))) return;
        _hidePortal();
    });
}

// ── API pública ────────────────────────────────────────────────

/**
 * Inicializa el comportamiento de tooltip informativo en un trigger.
 * @param {HTMLElement} triggerEl  — botón o ícono que activa el tooltip
 * @param {HTMLElement} contentEl  — elemento cuyo contenido se clona en el portal
 * @returns {() => void} función de limpieza
 */
export function initInfoTooltip(triggerEl, contentEl) {
    if (!triggerEl || !contentEl) return () => {};
    _ensureGlobal();

    const show  = ()  => _showPortal(triggerEl, contentEl);
    const hide  = ()  => { if (_trigger === triggerEl) _hidePortal(); };
    const sched = ()  => _scheduleHide(triggerEl);

    function onClick(e) {
        e.stopPropagation();
        _isOpen(triggerEl) ? hide() : show();
    }

    function onKeydown(e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); _isOpen(triggerEl) ? hide() : show(); }
    }

    triggerEl.addEventListener('mouseenter', show);
    triggerEl.addEventListener('mouseleave', sched);
    triggerEl.addEventListener('focus',      show);
    triggerEl.addEventListener('blur',       sched);
    triggerEl.addEventListener('click',      onClick);
    if (triggerEl.tagName !== 'BUTTON') triggerEl.addEventListener('keydown', onKeydown);

    return () => {
        triggerEl.removeEventListener('mouseenter', show);
        triggerEl.removeEventListener('mouseleave', sched);
        triggerEl.removeEventListener('focus',      show);
        triggerEl.removeEventListener('blur',       sched);
        triggerEl.removeEventListener('click',      onClick);
        if (triggerEl.tagName !== 'BUTTON') triggerEl.removeEventListener('keydown', onKeydown);
        hide();
    };
}
